import type {
  ClientMessage,
  PeerInfo,
  ProtocolErrorPayload,
  ServerMessage,
  UserSettings,
} from '@flowshare/protocol';
import {
  STORAGE_KEYS,
  createClientMessage,
  decodeChunkFrame,
  parseTransferMessage,
  type PeerConnectionState,
} from '@flowshare/protocol';
import { isRoomCode, normalizeRoomCode } from '@flowshare/shared';
import { toast } from 'sonner';

import { ApiError, closeRoomRequest, createRoomRequest } from '@/lib/api';
import {
  TransferManager,
  type TransferSink,
} from '@/lib/file-transfer/manager';
import { downloadStoredFile } from '@/lib/file-transfer/storage';
import type { SignalingClient, SignalingStatus } from '@/lib/signaling/client';
import { SignalingClient as Client } from '@/lib/signaling/client';
import { WebRtcMesh } from '@/lib/webrtc/mesh';
import type { PeerChannels } from '@/lib/webrtc/peer-session';
import { DataChannelTransport } from '@/lib/webrtc/transport';
import { buildRtcConfiguration } from '@/lib/webrtc/config';
import { useHistoryStore } from '@/stores/history-store';
import { useSessionStore } from '@/stores/session-store';
import { getSettingsSnapshot } from '@/stores/settings-store';
import {
  useTransferStore,
  type TransferRecord,
} from '@/stores/transfer-store';

interface StoredSession {
  code: string;
  hostToken: string;
  savedAt: number;
}

const SETTINGS_FOR_TRANSFERS = (): Pick<
  UserSettings,
  'autoAcceptTransfers' | 'autoDownload' | 'maxConcurrentTransfers'
> => {
  const settings = getSettingsSnapshot();
  return {
    autoAcceptTransfers: settings.autoAcceptTransfers,
    autoDownload: settings.autoDownload,
    maxConcurrentTransfers: settings.maxConcurrentTransfers,
  };
};

/**
 * Owns the live room: signaling socket, WebRTC mesh and transfer engine.
 * Zustand stores expose the state to React; this class is the imperative core
 * and deliberately lives outside the component tree so navigation does not
 * interrupt transfers.
 */
class RoomController {
  private signaling: SignalingClient | null = null;
  private mesh: WebRtcMesh | null = null;
  private hostToken: string | null = null;
  private roomCode: string | null = null;
  private deviceName = '';
  private mode: 'host' | 'guest' = 'guest';
  private latencyTimer: ReturnType<typeof setInterval> | null = null;
  private readonly transfers: TransferManager;

  constructor() {
    const sink: TransferSink = {
      upsert: (record) => useTransferStore.getState().upsert(record),
      patch: (id, patch) => useTransferStore.getState().patch(id, patch),
      remove: (id) => useTransferStore.getState().remove(id),
      finish: (record) => this.finishTransfer(record),
      notify: (message, kind) => {
        if (kind === 'error') toast.error(message);
        else if (kind === 'success') toast.success(message);
        else toast(message);
      },
    };

    this.transfers = new TransferManager({
      sink,
      getSettings: SETTINGS_FOR_TRANSFERS,
      onIncomingOffer: (record) => {
        toast.info(`${record.peerName} wants to send you a file`, {
          description: record.fileName,
        });
      },
      onAutoDownload: (record) => {
        void this.downloadTransfer(record.id, true);
      },
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Room lifecycle                                                         */
  /* ---------------------------------------------------------------------- */

  async createRoom(): Promise<string | null> {
    if (this.signaling) this.leaveRoom({ silent: true });
    const settings = getSettingsSnapshot();
    this.deviceName = settings.deviceName;
    this.mode = 'host';
    useSessionStore.getState().start('creating');

    try {
      const created = await createRoomRequest();
      this.hostToken = created.hostToken;
      this.roomCode = created.room.code;
      useSessionStore.getState().setHostToken(created.hostToken);
      this.persistSession();
      this.openSignaling(created.room.code);
      return created.room.code;
    } catch (error) {
      this.failWithError(error);
      return null;
    }
  }

  joinRoom(code: string): boolean {
    const normalized = normalizeRoomCode(code);
    if (!isRoomCode(normalized)) {
      toast.error('Enter a valid six-character room code.');
      return false;
    }

    if (this.signaling) this.leaveRoom({ silent: true });
    const settings = getSettingsSnapshot();
    this.deviceName = settings.deviceName;
    this.mode = 'guest';
    this.hostToken = null;
    this.roomCode = normalized;
    useSessionStore.getState().start('joining');
    this.openSignaling(normalized);
    return true;
  }

  /** Reclaim a room after a page reload using the token created with the room. */
  resumeHostSession(code: string): boolean {
    const normalized = normalizeRoomCode(code);
    const stored = this.readStoredSession();
    if (!stored || stored.code !== normalized) return false;
    if (Date.now() - stored.savedAt > 2 * 60 * 60 * 1000) return false;
    if (this.signaling && this.roomCode === normalized) return true;

    this.deviceName = getSettingsSnapshot().deviceName;
    this.mode = 'host';
    this.hostToken = stored.hostToken;
    this.roomCode = stored.code;
    useSessionStore.getState().start('joining');
    useSessionStore.getState().setHostToken(stored.hostToken);
    this.openSignaling(stored.code);
    return true;
  }

  leaveRoom(options: { silent?: boolean } = {}): void {
    try {
      this.signaling?.leave();
    } catch {
      // Ignore.
    }
    this.signaling = null;
    this.teardownPeers();
    useSessionStore.getState().reset();
    this.hostToken = null;
    this.roomCode = null;
    this.clearPersistedSession();

    if (!options.silent) toast.success('Left the room.');
  }

  async closeRoom(): Promise<void> {
    if (!this.roomCode || !this.hostToken) return;
    try {
      await closeRoomRequest(this.roomCode, this.hostToken);
      toast.success('Room closed. Every device was disconnected.');
      this.signaling?.dispose();
      this.signaling = null;
      this.teardownPeers();
      useSessionStore.getState().reset();
      this.hostToken = null;
      this.roomCode = null;
      this.clearPersistedSession();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'ROOM_NOT_FOUND') {
        this.leaveRoom({ silent: true });
        toast('That room was already closed.');
        return;
      }
      toast.error(error instanceof ApiError ? error.message : 'Could not close the room.');
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Transfers                                                              */
  /* ---------------------------------------------------------------------- */

  sendFiles(files: File[], peerIds: string[]): number {
    const state = useSessionStore.getState();
    const peers = peerIds
      .map((id) => {
        const peer = state.peers[id];
        return peer ? { id: peer.id, name: peer.name } : null;
      })
      .filter((peer): peer is { id: string; name: string } => peer !== null);

    return this.transfers.enqueue(files, peers);
  }

  cancelTransfer(id: string): void {
    this.transfers.cancel(id);
  }

  retryTransfer(id: string): boolean {
    const record = useTransferStore.getState().transfers[id];
    if (!record?.canRetry) {
      toast.error('This transfer cannot be retried.');
      return false;
    }
    return this.transfers.retry(id);
  }

  acceptIncoming(id: string): void {
    this.transfers.acceptIncoming(id);
  }

  declineIncoming(id: string): void {
    this.transfers.declineIncoming(id);
  }

  removePeer(peerId: string): boolean {
    if (!this.signaling) return false;
    const self = useSessionStore.getState().self;
    if (!self || self.role !== 'host') {
      toast.error('Only the host can remove devices.');
      return false;
    }
    this.signaling.send(createClientMessage('remove_peer', { peerId }));
    return true;
  }

  async downloadTransfer(id: string, silent = false): Promise<void> {
    const record = useTransferStore.getState().transfers[id];
    if (!record?.storageKey) {
      if (!silent) toast.error('The received file is no longer available locally.');
      return;
    }

    const ok = await downloadStoredFile(record.storageKey, record.fileName);
    if (!ok && !silent) toast.error('The received file is no longer available locally.');
  }

  /* ---------------------------------------------------------------------- */
  /* Signaling                                                              */
  /* ---------------------------------------------------------------------- */

  private openSignaling(code: string): void {
    this.roomCode = code;
    this.teardownPeers();

    const signaling = new Client({
      roomCode: code,
      buildHandshake: () => this.buildHandshake(),
      onMessage: (message) => this.handleServerMessage(message),
      onStatus: (status) => this.handleSignalingStatus(status),
      onFatal: (error) => this.handleFatal(error),
      onError: (error) => this.handleProtocolError(error),
      onLatency: (milliseconds) => this.handleLatency(milliseconds),
    });

    this.signaling = signaling;
    signaling.connect();
  }

  private buildHandshake(): ClientMessage {
    if (this.mode === 'host' && this.hostToken) {
      return createClientMessage('create_room', {
        hostToken: this.hostToken,
        deviceName: this.deviceName,
      });
    }

    return createClientMessage('join_room', {
      deviceName: this.deviceName,
      ...(this.hostToken ? { hostToken: this.hostToken } : {}),
    });
  }

  private handleServerMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'room_created':
      case 'room_joined': {
        const { room, self, peers } = message.payload;
        useSessionStore.getState().setConnected(room, self, peers);
        useSessionStore.getState().setHostToken(this.hostToken);
        this.buildMesh(self.id, peers);
        if (this.mode === 'host') this.persistSession();
        this.startLatencyPoll();
        break;
      }

      case 'peer_joined': {
        const { peer } = message.payload;
        const store = useSessionStore.getState();
        store.upsertPeer(peer);
        store.patchRoom({ peerCount: Object.keys(useSessionStore.getState().peers).length });
        this.mesh?.addPeer(peer.id);
        toast(`${peer.name} joined the room`, { icon: '🔗' });
        break;
      }

      case 'peer_left': {
        const { peerId, reason } = message.payload;
        const store = useSessionStore.getState();
        const peer = store.peers[peerId];
        store.removePeer(peerId);
        store.patchRoom({ peerCount: Object.keys(useSessionStore.getState().peers).length });
        this.mesh?.removePeer(peerId);
        this.transfers.detachTransport(peerId, 'The device left the room.');
        if (peer && reason !== 'room_closed') {
          toast(`${peer.name} left the room`);
        }
        break;
      }

      case 'offer':
        this.mesh?.handleDescription(message.payload.fromPeerId, message.payload.sdp);
        break;

      case 'answer':
        this.mesh?.handleDescription(message.payload.fromPeerId, message.payload.sdp);
        break;

      case 'ice_candidate':
        this.mesh?.handleCandidate(message.payload.fromPeerId, message.payload.candidate);
        break;

      case 'room_error':
        this.handleProtocolError(message.payload);
        break;

      case 'heartbeat_ack':
        break;
    }
  }

  private handleSignalingStatus(status: SignalingStatus): void {
    const store = useSessionStore.getState();

    if (status === 'reconnecting') {
      if (store.phase === 'connected') store.setPhase('reconnecting');
      return;
    }

    if (status === 'closed') {
      if (store.phase === 'error' || store.phase === 'ended') return;
      store.setPhase('ended');
    }
  }

  private handleFatal(error: ProtocolErrorPayload): void {
    const store = useSessionStore.getState();
    this.teardownPeers();
    this.clearLatencyPoll();
    store.setError({ code: error.code, message: error.message });
    toast.error(error.message);

    if (
      error.code === 'ROOM_CLOSED' ||
      error.code === 'ROOM_EXPIRED' ||
      error.code === 'ROOM_NOT_FOUND' ||
      error.code === 'UNAUTHORIZED'
    ) {
      this.clearPersistedSession();
    }
  }

  private handleProtocolError(error: ProtocolErrorPayload): void {
    if (error.code === 'PEER_NOT_FOUND') return;
    toast.error(error.message);
  }

  private handleLatency(milliseconds: number | null): void {
    useSessionStore.getState().setLatency(milliseconds);
  }

  /* ---------------------------------------------------------------------- */
  /* WebRTC mesh                                                            */
  /* ---------------------------------------------------------------------- */

  private buildMesh(selfPeerId: string, peers: PeerInfo[]): void {
    this.mesh?.destroy();
    this.transfers.detachAllTransports();
    this.clearLatencyPoll();

    const mesh = new WebRtcMesh({
      selfPeerId,
      getConfiguration: () => buildRtcConfiguration(getSettingsSnapshot()),
      sendSignal: (targetPeerId, signal) => {
        if (!this.signaling) return;
        if (signal.type === 'description') {
          const sdp = signal.description.sdp;
          if (!sdp) return;
          if (signal.description.type === 'offer') {
            this.signaling.sendOffer(targetPeerId, {
              type: 'offer',
              sdp,
            });
          } else {
            this.signaling.sendAnswer(targetPeerId, {
              type: 'answer',
              sdp,
            });
          }
        } else {
          this.signaling.sendIceCandidate(targetPeerId, signal.candidate);
        }
      },
      onPeerState: (peerId, state) => this.handlePeerState(peerId, state),
      onChannels: (peerId, channels) => this.handleChannels(peerId, channels),
      onChannelsLost: (peerId) => this.handleChannelsLost(peerId),
      onError: (peerId, message) => {
        console.warn(`WebRTC peer ${peerId}: ${message}`);
      },
    });

    this.mesh = mesh;
    mesh.syncPeers(peers.filter((peer) => peer.id !== selfPeerId).map((peer) => peer.id));
  }

  private handlePeerState(peerId: string, state: PeerConnectionState): void {
    const store = useSessionStore.getState();
    const existing = store.peers[peerId];
    if (!existing) return;

    store.patchPeer(peerId, {
      connectionState: state,
      connectedAt:
        state === 'connected' ? (existing.connectedAt ?? Date.now()) : existing.connectedAt,
    });

    if (state === 'failed' || state === 'closed') {
      this.transfers.detachTransport(peerId, 'The peer connection closed.');
    }
  }

  private handleChannels(peerId: string, channels: PeerChannels): void {
    const peer = useSessionStore.getState().peers[peerId];
    const transport = new DataChannelTransport(
      peerId,
      peer?.name ?? 'Device',
      channels.control,
      channels.data,
    );

    channels.control.onmessage = (event: MessageEvent) => {
      if (typeof event.data !== 'string') return;
      const parsed = parseTransferMessage(event.data);
      if (!parsed.ok) {
        console.warn('Invalid transfer control message', parsed.error.code);
        return;
      }
      this.transfers.handleControl(peerId, parsed.value);
    };

    channels.data.binaryType = 'arraybuffer';
    channels.data.onmessage = (event: MessageEvent) => {
      if (typeof event.data === 'string') {
        // Completion rides on the ordered data channel so it can never overtake
        // the final chunks. Accept only that message type here.
        const parsed = parseTransferMessage(event.data);
        if (parsed.ok && parsed.value.type === 'file_complete') {
          this.transfers.handleControl(peerId, parsed.value);
        }
        return;
      }
      const frame = decodeChunkFrame(event.data as ArrayBuffer);
      if (!frame) return;
      this.transfers.onChunk(peerId, frame);
    };

    this.transfers.attachTransport(transport);
    useSessionStore.getState().patchPeer(peerId, {
      connectionState: 'connected',
      connectedAt: useSessionStore.getState().peers[peerId]?.connectedAt ?? Date.now(),
    });
  }

  private handleChannelsLost(peerId: string): void {
    this.transfers.detachTransport(peerId, 'The data channel closed.');
    const peer = useSessionStore.getState().peers[peerId];
    if (peer) {
      useSessionStore.getState().patchPeer(peerId, { connectionState: 'disconnected' });
    }
  }

  private startLatencyPoll(): void {
    this.clearLatencyPoll();
    this.latencyTimer = setInterval(() => {
      const store = useSessionStore.getState();
      if (store.phase !== 'connected' && store.phase !== 'reconnecting') return;
      for (const peer of Object.values(store.peers)) {
        if (peer.isSelf || peer.connectionState !== 'connected') continue;
        void this.mesh?.peerRoundTripTime(peer.id).then((rtt) => {
          if (rtt !== null) {
            useSessionStore.getState().patchPeer(peer.id, { latencyMs: rtt });
          }
        });
      }
    }, 5_000);
  }

  private clearLatencyPoll(): void {
    if (this.latencyTimer) clearInterval(this.latencyTimer);
    this.latencyTimer = null;
  }

  private teardownPeers(): void {
    this.clearLatencyPoll();
    this.transfers.detachAllTransports();
    this.mesh?.destroy();
    this.mesh = null;
  }

  /* ---------------------------------------------------------------------- */
  /* History and persistence                                                */
  /* ---------------------------------------------------------------------- */

  private finishTransfer(record: TransferRecord): void {
    if (
      record.status !== 'completed' &&
      record.status !== 'failed' &&
      record.status !== 'cancelled'
    ) {
      return;
    }

    useHistoryStore.getState().add({
      id: record.id,
      fileName: record.fileName,
      fileSize: record.fileSize,
      direction: record.direction,
      peerName: record.peerName,
      status: record.status,
      startedAt: record.startedAt,
      completedAt: record.completedAt ?? Date.now(),
      sha256: record.sha256,
      storageKey: record.storageKey,
      error: record.error,
    });

    if (record.status === 'completed') {
      toast.success(
        record.direction === 'send'
          ? `Sent ${record.fileName}`
          : `Received ${record.fileName}`,
        {
          description:
            record.direction === 'send'
              ? `To ${record.peerName} · integrity verified`
              : `From ${record.peerName} · integrity verified`,
        },
      );
    } else if (record.status === 'failed') {
      toast.error(`${record.fileName} failed`, { description: record.error ?? undefined });
    }
  }

  private failWithError(error: unknown): void {
    const message =
      error instanceof ApiError ? error.message : 'Something went wrong. Please try again.';
    useSessionStore.getState().setError({
      code: error instanceof ApiError ? error.code : 'INTERNAL_ERROR',
      message,
    });
    toast.error(message);
  }

  private persistSession(): void {
    if (!this.roomCode || !this.hostToken) return;
    try {
      const value: StoredSession = {
        code: this.roomCode,
        hostToken: this.hostToken,
        savedAt: Date.now(),
      };
      sessionStorage.setItem(STORAGE_KEYS.session, JSON.stringify(value));
    } catch {
      // Private mode or quota; reconnection after reload simply won't work.
    }
  }

  private readStoredSession(): StoredSession | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEYS.session);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<StoredSession>;
      if (
        typeof parsed.code !== 'string' ||
        typeof parsed.hostToken !== 'string' ||
        typeof parsed.savedAt !== 'number' ||
        !isRoomCode(parsed.code)
      ) {
        return null;
      }
      return { code: parsed.code, hostToken: parsed.hostToken, savedAt: parsed.savedAt };
    } catch {
      return null;
    }
  }

  private clearPersistedSession(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.session);
    } catch {
      // Ignore.
    }
  }

  get activeRoomCode(): string | null {
    return this.roomCode;
  }

  getRoomLink(code: string): string {
    return `${window.location.origin}/r/${normalizeRoomCode(code)}`;
  }
}

export const roomController = new RoomController();
